import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../../components/ui/dropdown-menu";
import {
  ArrowLeft, Download, FileSpreadsheet, FileText, Search, Users,
  DollarSign, Clock, RefreshCw, Filter, Calendar, X, File, FileType, FileJson, BookOpen, Building,
  BarChart3, TrendingUp, AlertTriangle, Shield, Printer, Check, ChevronsUpDown
} from "lucide-react";
import { useAuth } from "../../App";
import { toast } from "sonner";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import { applyPlugin } from "jspdf-autotable";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { fmtBs, COLORS } from "./reportesUtils";

applyPlugin(jsPDF);

const PublicidadMorosa = () => {
  const navigate = useNavigate();
  const { API, token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [loading, setLoading] = useState(false);
  const [reporteData, setReporteData] = useState(null);
  const [pubMorosaTrimMin, setPubMorosaTrimMin] = useState("");
  const [pubMorosaClase, setPubMorosaClase] = useState("todas");
  const [pubMorosaTipoAviso, setPubMorosaTipoAviso] = useState("todos");
  const [pubMorosaTipoCobro, setPubMorosaTipoCobro] = useState("todos");
  const [pubMorosaFilters, setPubMorosaFilters] = useState({ rif: "", nombre: "", tipoCobro: "", tipo: "", medidas: "", ultimo: "", trimestres: "", monto: "" });

  useEffect(() => { fetchPublicidadMorosaData(); }, []);

  const fetchPublicidadMorosaData = async (aplicarFiltros = false) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (pubMorosaTrimMin) params.append("trimestres_mora_min", pubMorosaTrimMin);
      if (pubMorosaClase !== "todas") params.append("clase_contribuyente", pubMorosaClase);
      if (pubMorosaTipoAviso !== "todos") params.append("tipo_aviso", pubMorosaTipoAviso);
      if (pubMorosaTipoCobro !== "todos") params.append("tipo_cobro", pubMorosaTipoCobro);
      const url = `${API}/reportes/publicidad-morosa?${params.toString()}`;
      const response = await axios.get(url, { headers });
      setReporteData(response.data);
      if (aplicarFiltros) toast.success("Filtros aplicados correctamente");
    } catch (error) {
      toast.error("Error al cargar datos de publicidad morosa");
    } finally {
      setLoading(false);
    }
  };

  const limpiarFiltrosPubMorosa = () => {
    setPubMorosaTrimMin("");
    setPubMorosaClase("todas");
    setPubMorosaTipoAviso("todos");
    setPubMorosaTipoCobro("todos");
    setPubMorosaFilters({ rif: "", nombre: "", tipoCobro: "", tipo: "", medidas: "", ultimo: "", trimestres: "", monto: "" });
    setTimeout(() => fetchPublicidadMorosaData(false), 0);
    toast.info("Filtros eliminados");
  };

  const hayFiltrosPubMorosaActivos = pubMorosaTrimMin || pubMorosaClase !== "todas" || pubMorosaTipoAviso !== "todos" || pubMorosaTipoCobro !== "todos";
  const updatePubMorosaFilter = (key, value) => setPubMorosaFilters(prev => ({ ...prev, [key]: value }));
  const hayFiltrosPubMorosaColActivos = Object.values(pubMorosaFilters).some(v => v !== "");

  const filteredPubMorosaFilas = reporteData?.filas?.filter(f => {
    const pf = pubMorosaFilters;
    if (pf.rif && !(f.rif || "").toLowerCase().includes(pf.rif.toLowerCase())) return false;
    if (pf.nombre && !(f.nombre_razon_social || "").toLowerCase().includes(pf.nombre.toLowerCase())) return false;
    if (pf.tipoCobro && !(f.tipo_cobro || "").toLowerCase().includes(pf.tipoCobro.toLowerCase())) return false;
    if (pf.tipo && !(f.tipo_aviso || "").toLowerCase().includes(pf.tipo.toLowerCase())) return false;
    if (pf.medidas && !(f.medidas || "").toLowerCase().includes(pf.medidas.toLowerCase())) return false;
    if (pf.ultimo && !(f.ultimo_pago || "").toLowerCase().includes(pf.ultimo.toLowerCase())) return false;
    if (pf.trimestres && !String(f.trimestres_morosos || 0).includes(pf.trimestres)) return false;
    if (pf.monto && !String(f.monto_adeudado || 0).includes(pf.monto)) return false;
    return true;
  }) || [];

  const pubMorosaTotalesFiltrados = filteredPubMorosaFilas.reduce((acc, f) => ({
    trimestres_morosos: acc.trimestres_morosos + (f.trimestres_morosos || 0),
    monto_adeudado: acc.monto_adeudado + (f.monto_adeudado || 0),
  }), { trimestres_morosos: 0, monto_adeudado: 0 });

  const exportPubMorosaToExcel = () => {
    if (!reporteData) return;
    const wsData = [
      ["PUBLICIDAD MOROSA"], [],
      ["RIF", "CONTRIBUYENTE", "TIPO COBRO", "TIPO DE AVISO", "MEDIDAS", "ÚLTIMO TRIM. PAGADO", "TRIM. MOROSOS", "MONTO ADEUDADO"]
    ];
    filteredPubMorosaFilas.forEach(f => {
      wsData.push([f.rif, f.nombre_razon_social, f.tipo_cobro, f.tipo_aviso, f.medidas, f.ultimo_pago, f.trimestres_morosos, f.monto_adeudado]);
    });
    wsData.push([]);
    wsData.push(["", "", "", "", "", "TOTALES", filteredPubMorosaFilas.length + " registros", pubMorosaTotalesFiltrados.monto_adeudado]);
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    XLSX.utils.book_append_sheet(wb, ws, "PublicidadMorosa");
    XLSX.writeFile(wb, `Publicidad_Morosa_${new Date().toISOString().split("T")[0]}.xlsx`);
    toast.success("Exportado a Excel");
  };

  const exportPubMorosaToPDF = () => {
    if (!reporteData) return;
    const doc = new jsPDF("l", "mm", "a4");
    doc.setFontSize(16);
    doc.setTextColor(234, 88, 12);
    doc.text("PUBLICIDAD MOROSA", 14, 18);
    doc.setFontSize(9);
    doc.setTextColor(100);
    doc.text(`Total registros morosos: ${filteredPubMorosaFilas.length}`, 14, 25);
    const headCols = ["RIF", "Contribuyente", "Tipo Cobro", "Tipo Aviso", "Medidas", "Último Pago", "Trim. Morosos", "Monto Adeudado"];
    const body = filteredPubMorosaFilas.map(f => [f.rif, f.nombre_razon_social, f.tipo_cobro, f.tipo_aviso, f.medidas, f.ultimo_pago, f.trimestres_morosos, fmtBs(f.monto_adeudado)]);
    body.push(["", "", "", "", "", "TOTALES", filteredPubMorosaFilas.length + " registros", fmtBs(pubMorosaTotalesFiltrados.monto_adeudado)]);
    doc.autoTable({
      startY: 32, head: [headCols], body,
      styles: { fontSize: 7, cellPadding: 2 },
      headStyles: { fillColor: [234, 88, 12], textColor: 255 },
      alternateRowStyles: { fillColor: [255, 247, 237] },
      didParseCell: (data) => { if (data.row.index === body.length - 1) { data.cell.styles.fontStyle = "bold"; data.cell.styles.fillColor = [254, 215, 170]; } }
    });
    doc.save(`Publicidad_Morosa_${new Date().toISOString().split("T")[0]}.pdf`);
    toast.success("Exportado a PDF");
  };

  const exportPubMorosaToCSV = () => {
    if (!reporteData) return;
    const cols = ["RIF", "CONTRIBUYENTE", "TIPO COBRO", "TIPO AVISO", "MEDIDAS", "ÚLTIMO PAGO", "TRIM. MOROSOS", "MONTO ADEUDADO"];
    const rows = filteredPubMorosaFilas.map(f => [`"${f.rif}"`, `"${f.nombre_razon_social}"`, `"${f.tipo_cobro}"`, `"${f.tipo_aviso}"`, `"${f.medidas}"`, `"${f.ultimo_pago}"`, f.trimestres_morosos, f.monto_adeudado].join(","));
    const blob = new Blob([cols.join(",") + "\n" + rows.join("\n")], { type: "text/csv;charset=utf-8" });
    saveAs(blob, `Publicidad_Morosa_${new Date().toISOString().split("T")[0]}.csv`);
    toast.success("Exportado a CSV");
  };

  const tiposAvisoDisponibles = reporteData?.resumen?.tipos_aviso || [];
  return (
    <div className="space-y-6" data-testid="reporte-publicidad-morosa">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/reportes")} data-testid="pubmorosa-btn-volver">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
            <AlertTriangle className="w-6 h-6 text-orange-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Publicidad Morosa
            </h1>
            <p className="text-slate-500">Contribuyentes con avisos de publicidad con trimestres pendientes</p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={() => fetchPublicidadMorosaData(false)} disabled={loading} data-testid="pubmorosa-btn-actualizar">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" disabled={!reporteData} data-testid="pubmorosa-btn-exportar">
                <Download className="w-4 h-4 mr-2" />
                Exportar
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={exportPubMorosaToExcel} data-testid="pubmorosa-export-excel">
                <FileSpreadsheet className="w-4 h-4 mr-2" /> Excel (.xlsx)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={exportPubMorosaToPDF} data-testid="pubmorosa-export-pdf">
                <FileText className="w-4 h-4 mr-2" /> PDF
              </DropdownMenuItem>
              <DropdownMenuItem onClick={exportPubMorosaToCSV} data-testid="pubmorosa-export-csv">
                <File className="w-4 h-4 mr-2" /> CSV
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Filtros superiores */}
      <Card className="border-orange-200 bg-orange-50/30">
        <CardContent className="p-4">
          <div className="flex items-end gap-4 flex-wrap">
            <div className="w-[160px]">
              <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Trim. Morosos Mín.</Label>
              <Input type="number" min="1" value={pubMorosaTrimMin} onChange={(e) => setPubMorosaTrimMin(e.target.value)} className="bg-white" placeholder="Ej: 4" data-testid="pubmorosa-trim-min" />
            </div>
            <div className="w-[200px]">
              <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Clase Contribuyente</Label>
              <Select value={pubMorosaClase} onValueChange={setPubMorosaClase}>
                <SelectTrigger className="bg-white" data-testid="pubmorosa-clase">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todas">Todas</SelectItem>
                  <SelectItem value="Contribuyente con LAE">Con LAE</SelectItem>
                  <SelectItem value="Contribuyente sin LAE">Sin LAE</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-[200px]">
              <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Tipo de Aviso</Label>
              <Select value={pubMorosaTipoAviso} onValueChange={setPubMorosaTipoAviso}>
                <SelectTrigger className="bg-white" data-testid="pubmorosa-tipo-aviso">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  {tiposAvisoDisponibles.map(t => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-[160px]">
              <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Tipo de Cobro</Label>
              <Select value={pubMorosaTipoCobro} onValueChange={setPubMorosaTipoCobro}>
                <SelectTrigger className="bg-white" data-testid="pubmorosa-tipo-cobro">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="tasas">Solo Tasas</SelectItem>
                  <SelectItem value="avisos">Solo Avisos/Vallas</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <Button onClick={() => fetchPublicidadMorosaData(true)} disabled={loading} className="bg-orange-600 hover:bg-orange-700" data-testid="pubmorosa-btn-aplicar">
                <Search className="w-4 h-4 mr-2" /> Aplicar
              </Button>
              {hayFiltrosPubMorosaActivos && (
                <Button variant="outline" onClick={limpiarFiltrosPubMorosa} disabled={loading} data-testid="pubmorosa-btn-limpiar">
                  <X className="w-4 h-4 mr-2" /> Limpiar
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-orange-400" />
        </div>
      ) : reporteData ? (
        <>
          {/* Tarjetas resumen */}
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-slate-500 mb-1">Contribuyentes Morosos</p>
                <p className="text-2xl font-bold text-orange-700">{reporteData.resumen?.total_contribuyentes_morosos || 0}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-slate-500 mb-1">Registros Morosos</p>
                <p className="text-2xl font-bold text-slate-900">{reporteData.resumen?.total_registros_morosos || 0}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-slate-500 mb-1">Tasas</p>
                <p className="text-2xl font-bold text-blue-600">{reporteData.resumen?.total_tasas || 0}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-slate-500 mb-1">Avisos/Vallas</p>
                <p className="text-2xl font-bold text-purple-600">{reporteData.resumen?.total_avisos || 0}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-slate-500 mb-1">Monto Total Adeudado</p>
                <p className="text-lg font-bold text-red-600">{fmtBs(reporteData.resumen?.monto_total_adeudado)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs text-slate-500 mb-1">Prom. Trim. Morosos</p>
                <p className="text-2xl font-bold text-amber-600">{reporteData.resumen?.promedio_trimestres_morosos || 0}</p>
              </CardContent>
            </Card>
          </div>

          {/* Gráficos */}
          {reporteData.filas?.length > 0 && (() => {
            const filas = reporteData.filas;
            
            // Distribución por Tipo de Cobro (Tasa vs Aviso/Valla)
            const tipoCobro = {};
            filas.forEach(f => { const t = f.tipo_cobro || "Sin tipo"; tipoCobro[t] = (tipoCobro[t] || 0) + 1; });
            const dataPieCobro = Object.entries(tipoCobro).map(([nombre, cantidad]) => ({ nombre, cantidad }));

            // Distribución por Trimestres Morosos (agrupados)
            const trimGroup = { "1 trim.": 0, "2-3 trim.": 0, "4-6 trim.": 0, "7-12 trim.": 0, "+12 trim.": 0 };
            filas.forEach(f => {
              const t = f.trimestres_morosos || 0;
              if (t <= 1) trimGroup["1 trim."]++;
              else if (t <= 3) trimGroup["2-3 trim."]++;
              else if (t <= 6) trimGroup["4-6 trim."]++;
              else if (t <= 12) trimGroup["7-12 trim."]++;
              else trimGroup["+12 trim."]++;
            });
            const dataBarTrim = Object.entries(trimGroup).filter(([,v]) => v > 0).map(([nombre, cantidad]) => ({ nombre, cantidad }));

            // Distribución por Tipo de Aviso
            const tipoAviso = {};
            filas.forEach(f => { const t = f.tipo_aviso || "Sin tipo"; tipoAviso[t] = (tipoAviso[t] || 0) + 1; });
            const dataPieAviso = Object.entries(tipoAviso).sort((a,b) => b[1]-a[1]).slice(0, 8).map(([nombre, cantidad]) => ({ nombre: nombre.length > 20 ? nombre.substring(0,18) + "..." : nombre, cantidad }));

            // Top 10 contribuyentes con mayor monto
            const topMontos = [...filas].filter(f => f.monto_adeudado > 0).sort((a,b) => b.monto_adeudado - a.monto_adeudado).slice(0, 10)
              .map(f => ({ nombre: (f.nombre_razon_social || "").substring(0, 25), monto: f.monto_adeudado }));

            const pieOrange = ["#f97316", "#fb923c", "#fdba74", "#fed7aa", "#ea580c", "#c2410c", "#9a3412", "#7c2d12"];
            const piePurple = ["#8b5cf6", "#a78bfa", "#c4b5fd", "#ddd6fe", "#7c3aed", "#6d28d9", "#5b21b6", "#4c1d95"];

            return (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Pie: Tipo de Cobro */}
                <Card>
                  <CardContent className="p-4">
                    <h3 className="text-sm font-semibold text-slate-700 mb-3">Distribución por Tipo de Cobro</h3>
                    <ResponsiveContainer width="100%" height={260}>
                      <PieChart>
                        <Pie data={dataPieCobro} cx="50%" cy="50%" outerRadius={90} dataKey="cantidad" nameKey="nombre"
                          label={({ nombre, percent }) => `${nombre} ${(percent * 100).toFixed(0)}%`} labelLine={false}>
                          {dataPieCobro.map((entry, i) => (
                            <Cell key={i} fill={entry.nombre === "Tasa" ? "#3b82f6" : entry.nombre === "Aviso/Valla" ? "#a855f7" : pieOrange[i % pieOrange.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* Barras: Trimestres morosos agrupados */}
                <Card>
                  <CardContent className="p-4">
                    <h3 className="text-sm font-semibold text-slate-700 mb-3">Distribución por Trimestres de Mora</h3>
                    <ResponsiveContainer width="100%" height={260}>
                      <BarChart data={dataBarTrim}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="nombre" tick={{ fontSize: 11 }} />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="cantidad" name="Registros" fill="#f97316" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* Pie: Tipo de Aviso */}
                {dataPieAviso.length > 1 && (
                  <Card>
                    <CardContent className="p-4">
                      <h3 className="text-sm font-semibold text-slate-700 mb-3">Distribución por Tipo de Aviso</h3>
                      <ResponsiveContainer width="100%" height={260}>
                        <PieChart>
                          <Pie data={dataPieAviso} cx="50%" cy="50%" outerRadius={90} dataKey="cantidad" nameKey="nombre"
                            label={({ nombre, percent }) => `${(nombre || "").substring(0,12)} ${(percent * 100).toFixed(0)}%`} labelLine={false}>
                            {dataPieAviso.map((_, i) => (
                              <Cell key={i} fill={piePurple[i % piePurple.length]} />
                            ))}
                          </Pie>
                          <Tooltip />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                )}

                {/* Barras: Top contribuyentes con mayor deuda */}
                {topMontos.length > 0 && (
                  <Card>
                    <CardContent className="p-4">
                      <h3 className="text-sm font-semibold text-slate-700 mb-3">Top Contribuyentes con Mayor Deuda</h3>
                      <ResponsiveContainer width="100%" height={260}>
                        <BarChart data={topMontos} layout="vertical" margin={{ left: 10, right: 20 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis type="number" tickFormatter={(v) => `Bs.${(v/1000).toFixed(0)}k`} />
                          <YAxis dataKey="nombre" type="category" width={130} tick={{ fontSize: 10 }} />
                          <Tooltip formatter={(v) => fmtBs(v)} />
                          <Bar dataKey="monto" name="Monto Adeudado" fill="#ef4444" radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                )}
              </div>
            );
          })()}

          {/* Tabla */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">Detalle de Morosidad</CardTitle>
                  <CardDescription>{filteredPubMorosaFilas.length} de {reporteData.filas?.length || 0} registros</CardDescription>
                </div>
                {hayFiltrosPubMorosaColActivos && (
                  <Button variant="outline" size="sm" onClick={() => setPubMorosaFilters({ rif: "", nombre: "", tipoCobro: "", tipo: "", medidas: "", ultimo: "", trimestres: "", monto: "" })} data-testid="pubmorosa-limpiar-filtros-col">
                    <X className="w-4 h-4 mr-1" /> Limpiar filtros
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="pubmorosa-tabla">
                  <thead>
                    <tr className="border-b-2 border-orange-200 bg-orange-50">
                      <th className="text-left py-3 px-3 text-xs font-semibold text-orange-800 uppercase">RIF</th>
                      <th className="text-left py-3 px-3 text-xs font-semibold text-orange-800 uppercase">Contribuyente</th>
                      <th className="text-left py-3 px-3 text-xs font-semibold text-orange-800 uppercase">Tipo Cobro</th>
                      <th className="text-left py-3 px-3 text-xs font-semibold text-orange-800 uppercase">Tipo de Aviso</th>
                      <th className="text-left py-3 px-3 text-xs font-semibold text-orange-800 uppercase">Medidas</th>
                      <th className="text-left py-3 px-3 text-xs font-semibold text-orange-800 uppercase">Último Trim. Pagado</th>
                      <th className="text-right py-3 px-3 text-xs font-semibold text-orange-800 uppercase">Trim. Morosos</th>
                      <th className="text-right py-3 px-3 text-xs font-semibold text-orange-800 uppercase">Monto Adeudado</th>
                    </tr>
                    <tr className="bg-orange-50/60 border-b border-orange-100">
                      <th className="px-2 py-1.5"><Input className="h-7 text-xs" placeholder="Filtrar..." value={pubMorosaFilters.rif} onChange={(e) => updatePubMorosaFilter("rif", e.target.value)} data-testid="pubmorosa-filter-rif" /></th>
                      <th className="px-2 py-1.5"><Input className="h-7 text-xs" placeholder="Filtrar..." value={pubMorosaFilters.nombre} onChange={(e) => updatePubMorosaFilter("nombre", e.target.value)} data-testid="pubmorosa-filter-nombre" /></th>
                      <th className="px-2 py-1.5"><Input className="h-7 text-xs" placeholder="Filtrar..." value={pubMorosaFilters.tipoCobro} onChange={(e) => updatePubMorosaFilter("tipoCobro", e.target.value)} data-testid="pubmorosa-filter-tipocobro" /></th>
                      <th className="px-2 py-1.5"><Input className="h-7 text-xs" placeholder="Filtrar..." value={pubMorosaFilters.tipo} onChange={(e) => updatePubMorosaFilter("tipo", e.target.value)} data-testid="pubmorosa-filter-tipo" /></th>
                      <th className="px-2 py-1.5"><Input className="h-7 text-xs" placeholder="Filtrar..." value={pubMorosaFilters.medidas} onChange={(e) => updatePubMorosaFilter("medidas", e.target.value)} data-testid="pubmorosa-filter-medidas" /></th>
                      <th className="px-2 py-1.5"><Input className="h-7 text-xs" placeholder="Filtrar..." value={pubMorosaFilters.ultimo} onChange={(e) => updatePubMorosaFilter("ultimo", e.target.value)} data-testid="pubmorosa-filter-ultimo" /></th>
                      <th className="px-2 py-1.5"><Input className="h-7 text-xs text-right" placeholder="Filtrar..." value={pubMorosaFilters.trimestres} onChange={(e) => updatePubMorosaFilter("trimestres", e.target.value)} data-testid="pubmorosa-filter-trimestres" /></th>
                      <th className="px-2 py-1.5"><Input className="h-7 text-xs text-right" placeholder="Filtrar..." value={pubMorosaFilters.monto} onChange={(e) => updatePubMorosaFilter("monto", e.target.value)} data-testid="pubmorosa-filter-monto" /></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPubMorosaFilas.length === 0 ? (
                      <tr><td colSpan={8} className="text-center py-12 text-slate-400">No se encontraron registros morosos</td></tr>
                    ) : (
                      filteredPubMorosaFilas.map((f, idx) => (
                        <tr key={idx} className="border-b border-slate-100 hover:bg-orange-50/30 transition-colors">
                          <td className="py-2.5 px-3 text-sm font-mono text-slate-700">{f.rif}</td>
                          <td className="py-2.5 px-3 text-sm text-slate-800">{f.nombre_razon_social}</td>
                          <td className="py-2.5 px-3 text-sm">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${f.tipo_cobro === "Tasa" ? "bg-blue-100 text-blue-800" : "bg-purple-100 text-purple-800"}`}>
                              {f.tipo_cobro}
                            </span>
                          </td>
                          <td className="py-2.5 px-3 text-sm"><span className="px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800">{f.tipo_aviso}</span></td>
                          <td className="py-2.5 px-3 text-sm text-slate-600">{f.medidas || "-"}</td>
                          <td className="py-2.5 px-3 text-sm text-slate-700">{f.ultimo_pago || "Sin registro"}</td>
                          <td className="py-2.5 px-3 text-sm text-right">
                            <span className={`font-bold ${f.trimestres_morosos >= 8 ? 'text-red-600' : f.trimestres_morosos >= 4 ? 'text-orange-600' : 'text-amber-600'}`}>
                              {f.trimestres_morosos}
                            </span>
                          </td>
                          <td className="py-2.5 px-3 text-sm text-right font-semibold text-red-700">{fmtBs(f.monto_adeudado)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                  {filteredPubMorosaFilas.length > 0 && (
                    <tfoot>
                      <tr className="bg-orange-100 border-t-2 border-orange-300">
                        <td colSpan={6} className="py-3 px-3 text-sm font-bold text-orange-900 uppercase">Totales ({filteredPubMorosaFilas.length} registros)</td>
                        <td className="py-3 px-3 text-sm text-right font-bold text-orange-900">Prom: {(pubMorosaTotalesFiltrados.trimestres_morosos / Math.max(filteredPubMorosaFilas.length, 1)).toFixed(1)}</td>
                        <td className="py-3 px-3 text-sm text-right font-bold text-red-700">{fmtBs(pubMorosaTotalesFiltrados.monto_adeudado)}</td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      ) : (
        <Card>
          <CardContent className="p-12 text-center">
            <AlertTriangle className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No se pudieron cargar los datos del reporte</p>
            <Button variant="outline" onClick={() => fetchPublicidadMorosaData()} className="mt-4">
              <RefreshCw className="w-4 h-4 mr-2" /> Reintentar
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );

};

export default PublicidadMorosa;
